import { Controller } from "@hotwired/stimulus";

// The GO4LEARN checkout (`go4learn/shared/checkout`), redrawn on 2026-10-15 on the model of
// the GO4SCHOOL checkout (`mobile_money_checkout_controller.js`).
//
// It only does what the server cannot do between two requests :
// - recognise the operator from the number (prefixes served by `MobilePayments::Msisdn`),
//   pick its method, and say BEFORE paying when that operator is not accepted ;
// - show the quote of the picked method — every quote is rendered by the server, nothing
//   is computed here ;
// - keep « Payer » closed until the terms are ticked ;
// - reload the screen with the typed code, so the quote is the server's.
//
// ⚠️ Without this script the page still works : the default method's quote is visible, the
// terms box is `required`, and the form posts.
export default class extends Controller {
  static targets = ["phone", "phoneBox", "method", "quote", "refused", "cgv", "submit", "code", "amount", "badge", "manual"];
  static values = { prefixes: Object, offered: Array };

  connect() {
    this.refresh();
  }

  national() {
    let digits = (this.hasPhoneTarget ? this.phoneTarget.value : "").replace(/\D/g, "");
    while (digits.startsWith("237") && digits.length > 9) digits = digits.slice(3);
    return digits;
  }

  operatorOf(national) {
    if (!/^6\d{8}$/.test(national)) return null;
    for (const [operator, prefixes] of Object.entries(this.prefixesValue)) {
      if (prefixes.some((prefix) => national.startsWith(prefix))) return operator;
    }
    return null;
  }

  detect() {
    const operator = this.operatorOf(this.national());
    this.chosenOperator = null;
    this.detectedOperator = operator;
    this.refusedOperator = operator && !this.offeredValue.includes(operator) ? operator : null;
    if (operator && !this.refusedOperator) {
      const radio = this.methodTargets.find((input) => input.dataset.operator === operator);
      if (radio && !radio.checked) radio.checked = true;
    }
    if (this.hasPhoneBoxTarget) this.phoneBoxTarget.classList.toggle("is-ok", Boolean(operator && !this.refusedOperator));
    this.refresh();
  }

  pickMethod(event) {
    // A hand-picked operator wins over the prefix (a ported number) and shows its badge.
    this.chosenOperator = event?.target?.dataset?.operator || null;
    this.refusedOperator = null;
    this.refresh();
  }

  toggleManual() {
    if (this.hasManualTarget) this.manualTarget.hidden = !this.manualTarget.hidden;
  }

  refresh() {
    const picked = this.methodTargets.find((input) => input.checked)?.value;
    let total = null;
    this.quoteTargets.forEach((section) => {
      const shown = section.dataset.method === picked;
      section.hidden = !shown;
      if (shown) total = section.dataset.total;
    });
    const shownOperator = this.chosenOperator || (this.refusedOperator ? null : this.detectedOperator);
    this.badgeTargets.forEach((badge) => {
      badge.hidden = badge.dataset.operator !== shownOperator;
    });
    this.refusedTargets.forEach((note) => {
      note.hidden = note.dataset.operator !== this.refusedOperator;
    });
    if (!this.hasSubmitTarget) return;

    const accepted = !this.hasCgvTarget || this.cgvTarget.checked;
    this.submitTarget.disabled = !accepted || Boolean(this.refusedOperator);
    if (total && this.hasAmountTarget) this.amountTarget.textContent = total;
  }

  submit(event) {
    if (this.refusedOperator || (this.hasCgvTarget && !this.cgvTarget.checked)) event.preventDefault();
  }

  applyCode() {
    const url = new URL(window.location.href);
    const code = this.hasCodeTarget ? this.codeTarget.value.trim() : "";
    if (code) url.searchParams.set("code_apport", code);
    else url.searchParams.delete("code_apport");
    window.location.assign(url.toString());
  }
}
